import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Button } from '@liminal/web-ui/primitives/Button';
import { IconButton } from '@liminal/web-ui/primitives/IconButton';
import { Input } from '@liminal/web-ui/forms/Input';
import { WarningCard } from '@liminal/web-ui/feedback/WarningCard';
import { getErrorMessage } from '@/errors/messages';
import { useResolveLocationLink } from '@/hooks/useRewards';
import { isGoogleMapsLink } from '@/lib/rewards';
import {
  GOOGLE_MAPS_LINK_LABEL,
  LINK_LABEL_MAX,
  LINK_URI_MAX,
  MAX_LINKS,
  MAX_LOCATIONS,
  newRowKey,
  placeResolvedLocation,
  resolvedPlace,
  type CatalogueErrors,
  type CatalogueForm,
  type LinkRow,
} from '../catalogueForm';

/** The labels the app shows most, offered as one-click rows. */
const PRESETS = [GOOGLE_MAPS_LINK_LABEL, 'Instagram', 'Website'] as const;

/** What a link row's Google Maps link did, shown under the row. */
type Notice =
  | { readonly kind: 'resolving' }
  | { readonly kind: 'added'; readonly locationKey: string }
  | { readonly kind: 'nearby'; readonly label: string }
  | { readonly kind: 'full' }
  | { readonly kind: 'failed'; readonly message: string };

interface LinksEditorProps {
  readonly form: CatalogueForm;
  readonly setForm: Dispatch<SetStateAction<CatalogueForm>>;
  readonly errors: CatalogueErrors['links'];
}

/**
 * The partner's labelled links. A Google Maps link, pasted or typed, is sent
 * to the resolver once, and the place it names joins Locations unless a
 * location within 25 m is already there; the notice under the row can undo
 * it. A link the resolver cannot read says why and offers a blank location.
 */
export function LinksEditor({ form, setForm, errors }: LinksEditorProps) {
  const resolver = useResolveLocationLink();
  const [notices, setNotices] = useState<Readonly<Record<string, Notice>>>({});
  // The link each row last sent to the resolver, so a paste and the blur
  // after it resolve once, and a link that failed is not retried on every blur.
  const sent = useRef(new Map<string, string>());
  // The locations as last rendered: a resolve comes back after the operator
  // may have added one, and the 25 m check must see it.
  const locations = useRef(form.locations);
  useEffect(() => {
    locations.current = form.locations;
  }, [form.locations]);

  const full = form.links.length >= MAX_LINKS;

  const setNotice = (linkKey: string, notice: Notice | null) =>
    setNotices((current) => {
      const next = { ...current };
      if (notice) next[linkKey] = notice;
      else delete next[linkKey];
      return next;
    });

  const setLink = (key: string, patch: Partial<LinkRow>) =>
    setForm((f) => ({ ...f, links: f.links.map((row) => (row.key === key ? { ...row, ...patch } : row)) }));

  const addLink = (label: string) => {
    const key = newRowKey();
    setForm((f) => ({ ...f, links: [...f.links, { key, label, uri: '' }] }));
  };

  const removeLink = (key: string) => {
    setForm((f) => ({ ...f, links: f.links.filter((row) => row.key !== key) }));
    setNotice(key, null);
    sent.current.delete(key);
  };

  const resolve = async (row: LinkRow, text: string) => {
    const uri = text.trim();
    if (!isGoogleMapsLink(uri) || sent.current.get(row.key) === uri) return;
    sent.current.set(row.key, uri);
    setNotice(row.key, { kind: 'resolving' });
    try {
      const place = resolvedPlace(await resolver.mutateAsync(uri), row.label);
      const outcome = placeResolvedLocation(locations.current, place, newRowKey());
      if (outcome.kind === 'added') {
        setForm((f) => ({ ...f, locations: [...f.locations, outcome.row] }));
        setNotice(row.key, { kind: 'added', locationKey: outcome.row.key });
      } else if (outcome.kind === 'nearby') {
        setNotice(row.key, { kind: 'nearby', label: outcome.existing.label });
      } else {
        setNotice(row.key, { kind: 'full' });
      }
    } catch (e) {
      setNotice(row.key, { kind: 'failed', message: getErrorMessage(e) });
    }
  };

  const undo = (linkKey: string, locationKey: string) => {
    setForm((f) => ({ ...f, locations: f.locations.filter((location) => location.key !== locationKey) }));
    setNotice(linkKey, null);
  };

  const addLocationByHand = (row: LinkRow) => {
    if (form.locations.length >= MAX_LOCATIONS) {
      setNotice(row.key, { kind: 'full' });
      return;
    }
    const key = newRowKey();
    setForm((f) => ({
      ...f,
      locations: [...f.locations, { key, label: row.label.trim(), latitude: '', longitude: '', address: '', entry: { mode: 'address' } }],
    }));
    setNotice(row.key, null);
  };

  return (
    <Stack spacing={2}>
      {form.links.map((row) => {
        const notice = notices[row.key];
        return (
          <Stack key={row.key} spacing={0.5} data-testid="link-row">
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box sx={{ width: 180, flexShrink: 0 }}>
                <Input
                  aria-label="Link label"
                  value={row.label}
                  onChange={(label) => setLink(row.key, { label })}
                  placeholder="Label"
                  maxLength={LINK_LABEL_MAX}
                  error={errors[row.key]?.label}
                />
              </Box>
              {/* The paste is caught here, around the field: the shared Input takes no onPaste. */}
              <Box sx={{ flex: 1, minWidth: 0 }} onPaste={(event) => void resolve(row, event.clipboardData.getData('text'))}>
                <Input
                  aria-label="Link URL"
                  type="url"
                  value={row.uri}
                  onChange={(uri) => setLink(row.key, { uri })}
                  onBlur={() => void resolve(row, row.uri)}
                  placeholder="https://"
                  maxLength={LINK_URI_MAX}
                  error={errors[row.key]?.uri}
                />
              </Box>
              <Box sx={{ height: 56, display: 'flex', alignItems: 'center' }}>
                <IconButton icon="close" label="Remove link" size="sm" tone="muted" onClick={() => removeLink(row.key)} />
              </Box>
            </Stack>
            {notice?.kind === 'resolving' && (
              <WarningCard tone="info" compact>
                Reading the place from this link…
              </WarningCard>
            )}
            {notice?.kind === 'added' && (
              <WarningCard tone="info" compact action={{ label: 'Undo', onClick: () => undo(row.key, notice.locationKey) }}>
                Location added from this link
              </WarningCard>
            )}
            {notice?.kind === 'nearby' && (
              <WarningCard tone="info" compact>
                {`This place is already in Locations, as "${notice.label}".`}
              </WarningCard>
            )}
            {notice?.kind === 'full' && (
              <WarningCard tone="warning" compact>
                {`Locations are full at ${MAX_LOCATIONS}, so this link's place was not added.`}
              </WarningCard>
            )}
            {notice?.kind === 'failed' && (
              <WarningCard tone="error" compact action={{ label: 'Add location by hand', onClick: () => addLocationByHand(row) }}>
                {notice.message}
              </WarningCard>
            )}
          </Stack>
        );
      })}
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
        {PRESETS.map((label) => (
          <Button key={label} variant="secondary" size="sm" startIcon="plus" disabled={full} onClick={() => addLink(label)}>
            {label}
          </Button>
        ))}
        <Button variant="link" size="sm" startIcon="plus" disabled={full} onClick={() => addLink('')}>
          Other link
        </Button>
      </Stack>
      {full && (
        <Typography variant="caption" color="text.secondary">
          {`A partner can have at most ${MAX_LINKS} links.`}
        </Typography>
      )}
    </Stack>
  );
}
